import type { BatteryInput, SolveMode, CalculationMode, LoadInput, ChargerInput, EfficiencyInput, SafetyInput, PackConfig } from '@brt/shared-types'

export interface DemoDataset {
  name: string
  description: string
  battery: BatteryInput
  solveMode: SolveMode
  calculationMode: CalculationMode
  load: LoadInput
  charger: ChargerInput
  efficiency: EfficiencyInput
  safety: SafetyInput
  pack: PackConfig | null
}

/*
 * Dataset count justification: 5 datasets
 *
 * The engine has these distinct branching paths:
 * 1. Chemistry type (8 chemistries with different Peukert exponents, derating curves, self-discharge)
 * 2. Calculation mode (CURRENT_BASED vs POWER_BASED load summation)
 * 3. Derating factor combinations (DoD, SoC, SoH, temp, storage, efficiency, reserve)
 * 4. Solve modes (RUNTIME, BATTERY_SIZE, MAX_LOAD)
 *
 * 5 datasets cover:
 * - Minimal: simple LiFePO4, single load, ideal conditions (baseline)
 * - Typical: AGM UPS with multiple loads, efficiency, reserve margin (most common use)
 * - Stressed: Lead Acid at -10°C, aged battery, long storage, all derating active (worst case)
 * - Power-based: Li-Ion with watt loads (exercises POWER_BASED calculation path)
 * - High self-discharge: NiMH with 30-day storage, high C-rate (exercises self-discharge + Peukert)
 */

export const DEMO_DATASETS: readonly DemoDataset[] = [
  {
    name: 'Simple LiFePO4',
    description: '100 Ah LiFePO4 at 12.8V with a single 5A load. Ideal conditions — no efficiency loss, no reserve margin.',
    battery: {
      chemistry: 'LI_FEPO4',
      capacityValue: 100,
      capacityUnit: 'AH',
      voltage: 12.8,
      dod: 0.80,
      stateOfCharge: 1.0,
      stateOfHealth: 1.0,
      temperatureC: 25,
      cutoffVoltage: 2.5,
      ageCycles: 0,
      storageDays: 0,
    },
    solveMode: 'RUNTIME',
    calculationMode: 'CURRENT_BASED',
    load: {
      loads: [{
        id: 'load-demo-1',
        name: 'Router',
        loadValue: 5,
        loadUnit: 'A',
        enabled: true,
        isPreset: false,
      }],
    },
    charger: {
      chargerCurrentValue: 10,
      chargerVoltage: 12,
      chargerPower: 0,
      chargeEfficiency: 0.90,
    },
    efficiency: {
      efficiencyPercent: 0.93,
      includeEfficiency: false,
      description: 'DC-DC converter',
    },
    safety: {
      reserveMarginPercent: 0,
      includeReserve: false,
      minimumRuntimeTarget: 0,
      includeTargetCheck: false,
    },
    pack: null,
  },
  {
    name: 'AGM UPS Backup',
    description: '200 Ah AGM at 24V powering a router (2A), modem (1.5A), and WiFi AP (3A). Includes 85% inverter efficiency and 10% reserve margin.',
    battery: {
      chemistry: 'AGM',
      capacityValue: 200,
      capacityUnit: 'AH',
      voltage: 24,
      dod: 0.50,
      stateOfCharge: 1.0,
      stateOfHealth: 0.90,
      temperatureC: 25,
      cutoffVoltage: 1.75,
      ageCycles: 200,
      storageDays: 0,
    },
    solveMode: 'RUNTIME',
    calculationMode: 'CURRENT_BASED',
    load: {
      loads: [
        { id: 'load-demo-2a', name: 'Router', loadValue: 2, loadUnit: 'A', enabled: true, isPreset: false },
        { id: 'load-demo-2b', name: 'Modem', loadValue: 1.5, loadUnit: 'A', enabled: true, isPreset: false },
        { id: 'load-demo-2c', name: 'WiFi AP', loadValue: 3, loadUnit: 'A', enabled: true, isPreset: false },
      ],
    },
    charger: {
      chargerCurrentValue: 20,
      chargerVoltage: 24,
      chargerPower: 0,
      chargeEfficiency: 0.90,
    },
    efficiency: {
      efficiencyPercent: 0.85,
      includeEfficiency: true,
      description: 'Inverter',
    },
    safety: {
      reserveMarginPercent: 0.10,
      includeReserve: true,
      minimumRuntimeTarget: 8,
      includeTargetCheck: true,
    },
    pack: null,
  },
  {
    name: 'Stressed Lead Acid',
    description: '150 Ah flooded lead-acid at 12V, stored 90 days at -10°C, 70% SoH, 60% SoC. Maximum derating stress test with efficiency loss and 20% reserve.',
    battery: {
      chemistry: 'LEAD_ACID_FLOODED',
      capacityValue: 150,
      capacityUnit: 'AH',
      voltage: 12,
      dod: 0.50,
      stateOfCharge: 0.60,
      stateOfHealth: 0.70,
      temperatureC: -10,
      cutoffVoltage: 1.75,
      ageCycles: 400,
      storageDays: 90,
    },
    solveMode: 'RUNTIME',
    calculationMode: 'CURRENT_BASED',
    load: {
      loads: [{
        id: 'load-demo-3',
        name: 'Emergency Light',
        loadValue: 10,
        loadUnit: 'A',
        enabled: true,
        isPreset: false,
      }],
    },
    charger: {
      chargerCurrentValue: 15,
      chargerVoltage: 12,
      chargerPower: 0,
      chargeEfficiency: 0.85,
    },
    efficiency: {
      efficiencyPercent: 0.80,
      includeEfficiency: true,
      description: 'DC-DC converter',
    },
    safety: {
      reserveMarginPercent: 0.20,
      includeReserve: true,
      minimumRuntimeTarget: 4,
      includeTargetCheck: true,
    },
    pack: null,
  },
  {
    name: 'Power-based Li-Ion',
    description: '50 Ah Li-Ion at 14.8V with loads specified in watts: laptop (65W), monitor (30W), LED light (10W). Exercises the power-based calculation path.',
    battery: {
      chemistry: 'LI_ION',
      capacityValue: 50,
      capacityUnit: 'AH',
      voltage: 14.8,
      dod: 0.70,
      stateOfCharge: 0.90,
      stateOfHealth: 0.95,
      temperatureC: 30,
      cutoffVoltage: 2.8,
      ageCycles: 100,
      storageDays: 0,
    },
    solveMode: 'RUNTIME',
    calculationMode: 'POWER_BASED',
    load: {
      loads: [
        { id: 'load-demo-4a', name: 'Laptop', loadValue: 65, loadUnit: 'W', enabled: true, isPreset: false },
        { id: 'load-demo-4b', name: 'Monitor', loadValue: 30, loadUnit: 'W', enabled: true, isPreset: false },
        { id: 'load-demo-4c', name: 'LED Light', loadValue: 10, loadUnit: 'W', enabled: true, isPreset: false },
      ],
    },
    charger: {
      chargerCurrentValue: 5,
      chargerVoltage: 14.8,
      chargerPower: 0,
      chargeEfficiency: 0.90,
    },
    efficiency: {
      efficiencyPercent: 0.93,
      includeEfficiency: false,
      description: 'DC-DC converter',
    },
    safety: {
      reserveMarginPercent: 0,
      includeReserve: false,
      minimumRuntimeTarget: 0,
      includeTargetCheck: false,
    },
    pack: null,
  },
  {
    name: 'NiMH Storage Drain',
    description: '20 Ah NiMH at 9.6V after 30 days of storage. Tests the high self-discharge NiMH path with a moderate 2A load and 15% reserve.',
    battery: {
      chemistry: 'NI_MH',
      capacityValue: 20,
      capacityUnit: 'AH',
      voltage: 9.6,
      dod: 0.90,
      stateOfCharge: 1.0,
      stateOfHealth: 0.85,
      temperatureC: 20,
      cutoffVoltage: 1.0,
      ageCycles: 300,
      storageDays: 30,
    },
    solveMode: 'RUNTIME',
    calculationMode: 'CURRENT_BASED',
    load: {
      loads: [{
        id: 'load-demo-5',
        name: 'Sensor Array',
        loadValue: 2,
        loadUnit: 'A',
        enabled: true,
        isPreset: false,
      }],
    },
    charger: {
      chargerCurrentValue: 4,
      chargerVoltage: 9.6,
      chargerPower: 0,
      chargeEfficiency: 0.85,
    },
    efficiency: {
      efficiencyPercent: 0.90,
      includeEfficiency: true,
      description: 'Voltage regulator',
    },
    safety: {
      reserveMarginPercent: 0.15,
      includeReserve: true,
      minimumRuntimeTarget: 5,
      includeTargetCheck: true,
    },
    pack: null,
  },
] as const
